import type {
  AllowedAction,
  ChartPayload,
  ChartSeries,
  ConfidenceBand,
  DataClassification,
  DesiredDirection,
  FunctionalPermission,
  MetricStatus,
  MetricUnit,
  MetricValue,
  Money,
  QualitySummary,
  QueryMetadata,
  SourceStatus,
} from '@aurora-pvi/contracts';
import type { AuroraDataset, Connector } from '@aurora-pvi/seed';
import { correlationIdFor, money, round } from '@aurora-pvi/seed';
import type { ScopedData } from './filters';
import type { SeededPersona } from './types';

/**
 * The response envelope.
 *
 * Freshness is derived from the connectors that actually fed the response, not
 * asserted per endpoint. That is why the Finance ERP feed being down turns the
 * investment page into a source-failure state while leaving the process mapping
 * page perfectly healthy — which is the behaviour a real operator expects, and
 * the reason `contributingSources` is a list rather than a single flag.
 */

export const FINANCE_MASKED_FIELDS = [
  'annualRunCost',
  'totalCostOfOwnership',
  'costSummary',
  'benefitSummary',
  'investmentYtd',
  'businessValue',
  // AI savings are a financial claim like any other. Nulled rather than zeroed:
  // returning 0 would understate the product's AI impact to exactly the readers
  // least equipped to know the number was withheld rather than measured.
  'aiImpact.costSavings',
  'aiImpact.hoursSaved',
];

export const WORKFORCE_MASKED_FIELDS = ['contractorFte', 'vacancyCount', 'capacityPerIteration'];

export function maskedFieldsFor(user: SeededPersona): string[] {
  const fields: string[] = [];
  if (!user.canSeeRestrictedFinance) fields.push(...FINANCE_MASKED_FIELDS);
  if (!user.canSeeWorkforce) fields.push(...WORKFORCE_MASKED_FIELDS);
  return fields;
}

function connectorStatus(connector: Connector): MetricStatus {
  if (connector.connectionHealth === 'Down') return 'Error';
  if (connector.connectionHealth === 'Paused') return 'Unavailable';
  if (connector.isStale) return 'Stale';
  if (connector.connectionHealth === 'Degraded') return 'Partial';
  return 'Available';
}

const STATUS_SEVERITY: Record<MetricStatus, number> = {
  Available: 0,
  Partial: 1,
  Stale: 2,
  Unavailable: 3,
  Error: 4,
};

export function buildSourceStatus(
  dataset: AuroraDataset,
  sourceNames: readonly string[],
): SourceStatus {
  const connectors = dataset.connectors.filter((c) => sourceNames.includes(c.name));
  const contributingSources = connectors.map((connector) => ({
    sourceSystem: connector.name,
    status: connectorStatus(connector),
    refreshedAt: connector.lastSyncAt,
    recordCount: connector.recordsIngested,
  }));

  let overall: MetricStatus = 'Available';
  for (const source of contributingSources) {
    if (STATUS_SEVERITY[source.status] > STATUS_SEVERITY[overall]) overall = source.status;
  }

  const refreshedAt =
    contributingSources
      .map((s) => s.refreshedAt)
      .filter((value): value is string => value !== null)
      .sort()
      .at(-1) ?? null;

  const failed = connectors.find((c) => c.connectionHealth === 'Down');
  const failedRun = failed
    ? dataset.ingestionRuns.find(
        (run) => run.connectorIntegrationId === failed.integrationId && run.status === 'Failed',
      )
    : undefined;
  const lastGood = failed
    ? (dataset.ingestionRuns
        .filter(
          (run) =>
            run.connectorIntegrationId === failed.integrationId &&
            (run.status === 'Completed' || run.status === 'Warning'),
        )
        .map((run) => run.startedAt)
        .sort()
        .at(-1) ?? null)
    : null;

  return {
    overall,
    refreshedAt,
    lastGoodAt: lastGood,
    failedRunAt: failedRun?.startedAt ?? null,
    contributingSources,
  };
}

export function buildQuality(dataset: AuroraDataset, openIssueCount?: number): QualitySummary {
  const rules = dataset.dataQualityRules;
  const scored = (dimension: string): number => {
    const set = rules.filter((r) => r.dimension === dimension);
    if (set.length === 0) return 1;
    return round(set.filter((r) => r.outcome !== 'Failed').length / set.length, 4);
  };
  const passed = rules.filter((r) => r.outcome === 'Passed').length;
  return {
    score: rules.length === 0 ? null : round((passed / rules.length) * 100, 1),
    completeness: scored('Completeness'),
    validity: scored('Validity'),
    uniqueness: scored('Uniqueness'),
    consistency: scored('Consistency'),
    timeliness: scored('Timeliness'),
    openIssueCount:
      openIssueCount ?? dataset.dataQualityIssues.filter((i) => i.status === 'Open').length,
  };
}

export interface MetadataOptions {
  route: string;
  /** Registered source systems that actually contributed to this response. */
  sources: readonly string[];
  sequence?: number;
  confidence?: ConfidenceBand;
  asOf?: string | null;
  openIssueCount?: number;
  actions?: {
    action: string;
    permission: FunctionalPermission;
    enabled: boolean;
    disabledReason?: string;
  }[];
}

export function buildMetadata(
  dataset: AuroraDataset,
  user: SeededPersona,
  scope: ScopedData,
  options: MetadataOptions,
): QueryMetadata {
  const allowedActions: AllowedAction[] = (options.actions ?? []).map((action) => ({
    action: action.action,
    permission: action.permission,
    enabled: action.enabled,
    disabledReason: action.disabledReason ?? null,
  }));

  return {
    correlationId: correlationIdFor(options.route, user.personaId, options.sequence ?? 1),
    periodStart: dataset.periodStart,
    periodEnd: dataset.periodEnd,
    comparisonPeriodStart: `${dataset.fiscalYear - 1}-01-01`,
    comparisonPeriodEnd: `${dataset.fiscalYear - 1}-05-20`,
    asOf: options.asOf ?? null,
    sourceStatus: buildSourceStatus(dataset, options.sources),
    quality: buildQuality(dataset, options.openIssueCount),
    confidence: options.confidence ?? 'Medium',
    scope: {
      scopeId: scope.scopeId,
      scopeType: scope.scopeType,
      scopeLabel: scope.scopeLabel,
      isNarrowed: scope.isNarrowed,
      maskedFields: maskedFieldsFor(user),
    },
    allowedActions,
    // Never omitted. Seed data has to be visibly labelled wherever it appears.
    isSyntheticData: true,
  };
}

// ── Metric helpers ───────────────────────────────────────────────────────────

export interface MetricInput {
  metricId: string;
  label: string;
  value: number | string | Money | null;
  unit: MetricUnit;
  dataset: AuroraDataset;
  subjectId?: string | null;
  subjectType?: string | null;
  comparisonValue?: number | string | Money | null;
  comparisonLabel?: string | null;
  status?: MetricStatus;
  definitionVersion?: string;
  sourceMode?: MetricValue['sourceMode'];
  refreshedAt?: string | null;
  qualityScore?: number | null;
  confidence?: ConfidenceBand | null;
  desiredDirection?: DesiredDirection;
  sensitivity?: DataClassification;
  drilldowns?: { dimension: string; label: string; route: string }[];
  unavailableReason?: string | null;
}

export function metricValue(input: MetricInput): MetricValue {
  const status: MetricStatus = input.status ?? (input.value === null ? 'Unavailable' : 'Available');
  return {
    metricId: input.metricId,
    label: input.label,
    subjectId: input.subjectId ?? null,
    subjectType: input.subjectType ?? null,
    // A metric that is not Available carries no value. Coercing to zero here
    // would make "we could not read the finance feed" indistinguishable from
    // "this product costs nothing", which is the specific failure the contract
    // exists to prevent.
    value: status === 'Available' || status === 'Partial' ? input.value : null,
    unit: input.unit,
    periodStart: input.dataset.periodStart,
    periodEnd: input.dataset.periodEnd,
    comparisonValue: input.comparisonValue ?? null,
    comparisonLabel: input.comparisonLabel ?? 'vs prior year',
    status,
    definitionVersion: input.definitionVersion ?? 'MET-2026.1',
    sourceMode: input.sourceMode ?? 'Calculated',
    sourceRunId: null,
    // A calculated metric is exactly as fresh as the data it was calculated
    // from, so the dataset's own asOf is the honest default. Leaving this null
    // made every tile report "never refreshed", which reads as a broken feed
    // rather than as the derived figure it actually is.
    refreshedAt: input.refreshedAt ?? `${input.dataset.asOf}T08:30:00Z`,
    qualityScore: input.qualityScore ?? 88,
    confidence: input.confidence ?? 'Medium',
    desiredDirection: input.desiredDirection ?? 'Context',
    sensitivity: input.sensitivity ?? 'Internal',
    allowedDrilldowns: input.drilldowns ?? [],
    unavailableReason:
      status === 'Available' || status === 'Partial' ? null : (input.unavailableReason ?? null),
  };
}

/** A currency metric that becomes `Unavailable` — never zero — when masked. */
export function currencyMetric(
  input: Omit<MetricInput, 'value' | 'unit'> & { cents: number; user: SeededPersona },
): MetricValue {
  if (!input.user.canSeeRestrictedFinance) {
    return metricValue({
      ...input,
      value: null,
      unit: 'currency',
      status: 'Unavailable',
      sensitivity: 'Restricted',
      unavailableReason:
        'Financial values are restricted for your role. Ask a finance business partner for access.',
    });
  }
  return metricValue({
    ...input,
    value: money(input.cents),
    unit: 'currency',
    sensitivity: 'Confidential',
  });
}

export function moneyOrNull(user: SeededPersona, cents: number): Money | null {
  return user.canSeeRestrictedFinance ? money(cents) : null;
}

export function numberOrNull(allowed: boolean, value: number): number | null {
  return allowed ? value : null;
}

// ── Chart helper ─────────────────────────────────────────────────────────────

export function chartPayload(input: {
  chartId: string;
  title: string;
  accessibleSummary: string;
  series: ChartSeries[];
  xLabel?: string;
  metricIds?: string[];
  status?: MetricStatus;
  refreshedAt?: string | null;
}): ChartPayload {
  const xs: (string | number)[] = [];
  for (const series of input.series) {
    for (const point of series.points) if (!xs.includes(point.x)) xs.push(point.x);
  }
  return {
    chartId: input.chartId,
    title: input.title,
    accessibleSummary: input.accessibleSummary,
    series: input.series,
    // The table equivalent is generated from the same series, so a chart can
    // never drift from its accessible alternative.
    tableColumns: [
      { key: 'x', label: input.xLabel ?? 'Period' },
      ...input.series.map((series) => ({ key: series.seriesId, label: series.label })),
    ],
    tableRows: xs.map((x) => {
      const row: Record<string, string | number | null> = { x: x };
      for (const series of input.series) {
        row[series.seriesId] = series.points.find((p) => p.x === x)?.y ?? null;
      }
      return row;
    }),
    metricIds: input.metricIds ?? [],
    status: input.status ?? 'Available',
    refreshedAt: input.refreshedAt ?? null,
  };
}
